import { memo, useState, useEffect, useCallback, useRef } from 'react';

const COLORS = [
  '#FFD700', // Gold
  '#FF1744', // Red
  '#00E5FF', // Cyan
  '#76FF03', // Green
  '#FF6B6B', // Coral
  '#4ECDC4', // Turquoise
  '#FFEB3B', // Yellow
  '#E040FB', // Purple
  '#FF9800', // Orange
  '#F50057', // Pink
  '#00BCD4', // Light Blue
  '#8BC34A', // Light Green
];

const MAX_CONCURRENT_FIREWORKS = 3;
const ROCKET_DURATION = 1000; // 1 second for rocket to reach explosion point
const EXPLOSION_DURATION = 2500; // 2.5 seconds for explosion to fade
const LAUNCH_INTERVAL = 1200; // New firework every 1.2 seconds

/**
 * Realistic fireworks animation for New Year celebration
 * Up to 3 simultaneous fireworks, full screen coverage, realistic explosion pattern
 */
function FireworksAnimation() {
  const [fireworks, setFireworks] = useState([]);
  const activeCountRef = useRef(0);

  const createFirework = useCallback(() => {
    // Don't create more than MAX_CONCURRENT_FIREWORKS
    if (activeCountRef.current >= MAX_CONCURRENT_FIREWORKS) {
      return;
    }

    activeCountRef.current += 1;

    const fireworkId = Date.now() + Math.random();
    // Random position across full screen width (10% to 90%)
    const x = 10 + Math.random() * 80;
    // Random explosion height (25% to 55% from top, as vh for rocket travel)
    const explosionVh = 45 + Math.random() * 30; // 45-75vh travel distance
    const explosionY = 100 - explosionVh; // Convert to top percentage
    // Random color
    const color = COLORS[Math.floor(Math.random() * COLORS.length)];
    // Secondary color for variety
    const color2 = COLORS[Math.floor(Math.random() * COLORS.length)];

    // Add rocket to fireworks
    setFireworks(prev => [...prev, {
      id: fireworkId,
      type: 'rocket',
      x,
      explosionVh,
      color,
    }]);

    // After rocket reaches explosion point, create particles
    setTimeout(() => {
      // Remove rocket
      setFireworks(prev => prev.filter(f => f.id !== fireworkId));

      const particles = [];
      const particleCount = 60 + Math.floor(Math.random() * 40); // 60-100 particles

      for (let i = 0; i < particleCount; i++) {
        // Radial angle for circular explosion
        const angle = (i / particleCount) * Math.PI * 2;
        // Random distance for natural spread (150-300px)
        const distance = 150 + Math.random() * 150;
        // Random size variation
        const size = 3 + Math.random() * 4;
        // Alternate colors for variety
        const particleColor = i % 3 === 0 ? color2 : color;
        // Random duration for natural fade
        const duration = 1.5 + Math.random() * 1;
        // Small random angle offset for more natural look
        const angleOffset = (Math.random() - 0.5) * 0.3;

        particles.push({
          id: `${fireworkId}-particle-${i}`,
          type: 'particle',
          fireworkId,
          x,
          y: explosionY,
          angle: angle + angleOffset,
          distance,
          size,
          color: particleColor,
          duration,
          // Add gravity effect - particles fall slightly
          gravity: 0.3 + Math.random() * 0.2,
        });
      }

      // Add some sparkle particles (smaller, faster)
      for (let i = 0; i < 30; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = 50 + Math.random() * 100;

        particles.push({
          id: `${fireworkId}-sparkle-${i}`,
          type: 'particle',
          fireworkId,
          x,
          y: explosionY,
          angle,
          distance,
          size: 2,
          color: '#FFFFFF',
          duration: 0.8 + Math.random() * 0.5,
          gravity: 0.1,
          isSparkle: true,
        });
      }

      // Add particles
      setFireworks(prev => [...prev, ...particles]);

      // Clear particles after animation
      setTimeout(() => {
        setFireworks(prev => prev.filter(f => f.fireworkId !== fireworkId));
        activeCountRef.current -= 1;
      }, EXPLOSION_DURATION);
    }, ROCKET_DURATION);
  }, []);

  useEffect(() => {
    // Start first firework immediately
    createFirework();

    // Create new firework at staggered intervals
    const interval = setInterval(createFirework, LAUNCH_INTERVAL);

    return () => clearInterval(interval);
  }, [createFirework]);

  const rockets = fireworks.filter(f => f.type === 'rocket');
  const particles = fireworks.filter(f => f.type === 'particle');

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {/* Rockets */}
      {rockets.map((rocket) => (
        <div
          key={rocket.id}
          style={{
            position: 'absolute',
            left: `${rocket.x}%`,
            bottom: '0',
            width: '4px',
            height: '4px',
            backgroundColor: rocket.color,
            borderRadius: '50%',
            boxShadow: `0 0 10px ${rocket.color}, 0 0 20px ${rocket.color}, 0 0 30px ${rocket.color}`,
            animation: `rocketLaunch ${ROCKET_DURATION}ms ease-out forwards`,
            '--travelDistance': `${rocket.explosionVh}vh`,
          }}
        />
      ))}

      {/* Explosion particles */}
      {particles.map((particle) => {
        const endX = Math.cos(particle.angle) * particle.distance;
        const endY = Math.sin(particle.angle) * particle.distance + (particle.gravity * particle.distance);

        return (
          <div
            key={particle.id}
            style={{
              position: 'absolute',
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              backgroundColor: particle.color,
              borderRadius: '50%',
              boxShadow: particle.isSparkle
                ? `0 0 4px ${particle.color}`
                : `0 0 8px ${particle.color}, 0 0 16px ${particle.color}`,
              animation: `particleExplode ${particle.duration}s ease-out forwards`,
              '--endX': `${endX}px`,
              '--endY': `${endY}px`,
            }}
          />
        );
      })}

      {/* CSS Keyframes */}
      <style>
        {`
          @keyframes rocketLaunch {
            0% {
              transform: translateY(0);
              opacity: 1;
            }
            90% {
              opacity: 1;
            }
            100% {
              transform: translateY(calc(-1 * var(--travelDistance)));
              opacity: 0;
            }
          }

          @keyframes particleExplode {
            0% {
              transform: translate(0, 0) scale(1);
              opacity: 1;
            }
            70% {
              opacity: 0.8;
            }
            100% {
              transform: translate(var(--endX), var(--endY)) scale(0);
              opacity: 0;
            }
          }
        `}
      </style>
    </div>
  );
}

export default memo(FireworksAnimation);

